import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { FieldsetInput } from '../../shared/FieldsetInput/FieldsetInput';
import { FieldsetSelect, SelectKeyValue } from '../../shared/FieldsetSelect/FieldsetSelect';
import { ExitIcon } from '../../shared/Icons/index';
import { Button } from '../../shared/button/Button';
import './EditReport.scss';
import { EditReportFormValidationSummary, EditReportModel } from './editReportInterfaces';
import { EditReportFields, ValidateEditReportField } from './editReportValidation';

interface EditReportProps {
  id: string;
  categoryId: string;
  categoryName: string;
  reportName: string;
  handleCancel: () => void;
  editReport: (editedReport: EditReportModel) => Promise<void>;
}

export const EditReport = (props: EditReportProps) => {
  const { handleCancel, id, categoryId, reportName, editReport, categoryName } = props;
  const [editReportModel, setEditReportModel] = useState<EditReportModel>({
    id: '',
    categoryId: '',
    name: '',
  });
  const { categories } = useSelector((state: RootState) => state.categories);
  const [categoriesSorted, setCategoriesSorted] = useState<SelectKeyValue[]>([
    ...categories.map((c) => {
      return { key: c.id, value: c.name };
    }),
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const [editReportValidationSummary, setEditReportValidationSummary] = useState<EditReportFormValidationSummary>({
    validationErrors: {
      categoryId: '',
      name: '',
    },
    categoryIdValid: true,
    nameValid: true,
    formValid: true,
  });

  useEffect(() => {
    setEditReportModel({
      id: id,
      categoryId: categoryId,
      name: reportName,
    });
  }, [categoryId, id, reportName]);

  useEffect(() => {
    setCategoriesSorted(
      [
        ...categories.map((c) => {
          return { key: c.id, value: c.name };
        }),
      ].sort((a, b) => a.value.localeCompare(b.value))
    );
  }, [categories]);

  const handleCloseModal = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  const handleEditReport = async () => {
    if (!editReportValidationSummary.formValid) return;

    try {
      setIsLoading(true);
      await editReport(editReportModel);
      handleCloseModal();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryValidation = useCallback(() => {
    ValidateEditReportField(
      EditReportFields.categoryId,
      editReportModel.categoryId,
      editReportValidationSummary,
      setEditReportValidationSummary
    );
  }, [editReportModel.categoryId, editReportValidationSummary]);

  const handleNameValidation = useCallback(() => {
    ValidateEditReportField(
      EditReportFields.name,
      editReportModel.name,
      editReportValidationSummary,
      setEditReportValidationSummary
    );
  }, [editReportModel.name, editReportValidationSummary]);

  const handleCategoryIdChange = useCallback(
    (event: FormEvent<HTMLSelectElement>) => {
      setEditReportModel({
        ...editReportModel,
        categoryId: event.currentTarget.value,
      });

      ValidateEditReportField(
        EditReportFields.categoryId,
        event.currentTarget.value,
        editReportValidationSummary,
        setEditReportValidationSummary
      );
    },
    [editReportModel, editReportValidationSummary]
  );

  const handleReportNameChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      setEditReportModel({
        ...editReportModel,
        name: event.currentTarget.value,
      });

      ValidateEditReportField(
        EditReportFields.name,
        event.currentTarget.value,
        editReportValidationSummary,
        setEditReportValidationSummary
      );
    },
    [editReportModel, editReportValidationSummary]
  );

  return (
    <section className={'edit-report-container opened'}>
      <dialog open={true} className='modal'>
        <header>
          <h2>{`Edit ${categoryName} > ${reportName}`}</h2>
          <button onClick={handleCloseModal} data-testid='close-button'>
            <ExitIcon />
          </button>
        </header>
        <main>
          <p className='form-text'>
            <strong>Complete the the fields below to edit the report.</strong>
          </p>
          <FieldsetSelect
            id='category-id'
            label='Category'
            onChange={handleCategoryIdChange}
            focus={true}
            name='category-id'
            placeholder='Category'
            dataTestId='select-category'
            items={categoriesSorted}
            value={editReportModel.categoryId}
            onBlur={handleCategoryValidation}
            validationMessage={editReportValidationSummary.validationErrors.categoryId}
          />

          <FieldsetInput
            label='Report name'
            type='text'
            id='report-name'
            onChange={handleReportNameChange}
            value={editReportModel.name}
            name='report-name'
            onBlur={handleNameValidation}
            validationMessage={editReportValidationSummary.validationErrors.name}
          />

          <div className='buttons-container'>
            <Button
              lKey='Cancel'
              type='button'
              className='cancel-button'
              onClick={handleCloseModal}
              datatestid='cancel-button'
              isDisabled={isLoading}
            />

            <Button
              lKey='Submit'
              type='button'
              className='submit-button'
              datatestid='submit-button'
              isDisabled={false}
              isLoading={isLoading}
              onClick={async() => handleEditReport()}
            />
          </div>
        </main>
      </dialog>
    </section>
  );
};
